/**
 * The People page's requests, and the pure rules behind its Google Contacts
 * panel. Share tokens are minted on the server with the SHARE_LINK_SECRET the
 * browser never sees; this module only asks for a link. Likewise the Google
 * refresh token never reaches the browser: connect is a redirect through
 * Google that ends on the server, which sends the browser back to
 * `/people?google=connected` (or `?google=error&reason=<code>`).
 */
import { authedFetch } from "./api";
import { orThrow } from "./http";

export interface PersonRow {
  id: number;
  name: string;
  email: string | null;
  avatar_url: string | null;
  linked_to_google: boolean;
  hide_on_share: boolean;
  /** Games this person is tagged on. */
  events: number;
}

export interface PeopleDto {
  people: PersonRow[];
  /** null when Google Contacts is not connected. */
  google: { user_email: string | null; connected_at: string } | null;
  /** False when SHARE_LINK_SECRET is missing, so Share can say why it cannot. */
  share_configured: boolean;
}

/** One row of the import checklist. */
export interface GoogleContactRow {
  resource_name: string;
  name: string;
  email: string | null;
  photo_url: string | null;
  /** Linked to, or named like, a person already on file: shown, not selectable. */
  already_added: boolean;
}

export interface SyncResult {
  linked: number;
  linked_no_photo: number;
}

export interface ImportResult {
  added: number;
  /** Resource names the server no longer knows (a stale checklist). */
  unknown: string[];
  already_added: string[];
}

export const SHARE_DAYS = [7, 30, 90] as const;

export async function fetchPeople(): Promise<PeopleDto> {
  const res = await authedFetch("/api/people");
  return (await orThrow(res, "Could not load people")) as PeopleDto;
}

async function send(path: string, method: string, body: unknown, what: string): Promise<unknown> {
  const res = await authedFetch(path, {
    method,
    headers: { "Content-Type": "application/json" },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  return orThrow(res, what);
}

export const addPerson = (name: string, email: string) => send("/api/people", "POST", { name, email }, "Could not add the person");

export const updatePerson = (id: number, p: { name: string; email: string; hide_on_share: boolean }) =>
  send(`/api/people/${id}`, "PATCH", p, "Could not save the person");

export const deletePerson = (id: number) => send(`/api/people/${id}`, "DELETE", undefined, "Could not delete the person");

export async function shareLink(id: number, days: number): Promise<string> {
  const body = (await send(`/api/people/${id}/share`, "POST", { days }, "Could not make a share link")) as { url: string };
  return body.url;
}

// ---- Google Contacts -------------------------------------------------------

/** Google's consent URL. The caller navigates the whole page there. */
export async function startGoogleConnect(): Promise<string> {
  const body = (await send("/api/people/google/connect/start", "POST", undefined, "Could not start connecting Google")) as { auth_url: string };
  return body.auth_url;
}

export const disconnectGoogle = () => send("/api/people/google", "DELETE", undefined, "Could not disconnect Google");

/** Cached on the server for an hour; `refresh` asks Google again. */
export async function fetchGoogleContacts(refresh = false): Promise<GoogleContactRow[]> {
  const res = await authedFetch(`/api/people/google/contacts${refresh ? "?refresh=1" : ""}`);
  return (await orThrow(res, "Could not load Google contacts")) as GoogleContactRow[];
}

export const syncGooglePhotos = async () => (await send("/api/people/google/sync", "POST", undefined, "Could not sync photos")) as SyncResult;

export const importGoogleContacts = async (resourceNames: string[]) =>
  (await send("/api/people/google/import", "POST", { resource_names: resourceNames }, "Could not import contacts")) as ImportResult;

/** Contacts whose name or email contains the query, ignoring case. */
export function filterContacts(rows: GoogleContactRow[], query: string): GoogleContactRow[] {
  const q = query.trim().toLowerCase();
  if (!q) return rows;
  return rows.filter((r) => r.name.toLowerCase().includes(q) || (r.email ?? "").toLowerCase().includes(q));
}

/** The selection after tapping a row. An already-added contact never joins it. */
export function toggleContact(selected: ReadonlySet<string>, row: GoogleContactRow): Set<string> {
  const next = new Set(selected);
  if (row.already_added) return next;
  if (next.has(row.resource_name)) next.delete(row.resource_name);
  else next.add(row.resource_name);
  return next;
}

/** What "Add N" sends: selected, still listed and not already added, in list order. */
export function selectedForImport(selected: ReadonlySet<string>, rows: GoogleContactRow[]): string[] {
  return rows.filter((r) => !r.already_added && selected.has(r.resource_name)).map((r) => r.resource_name);
}

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

export function syncMessage(r: SyncResult): string {
  if (!r.linked) return "Nothing new to link";
  return `Linked ${plural(r.linked, "contact", "contacts")}${r.linked_no_photo ? ` (${r.linked_no_photo} without a photo)` : ""}`;
}

export function importMessage(r: ImportResult): string {
  return r.added ? `Added ${plural(r.added, "person", "people")}` : "Nothing new to add";
}

/** Why a connect failed, by the server's `reason` code. */
const CONNECT_ERRORS: Record<string, string> = {
  denied: "You cancelled at Google, so nothing was connected.",
  expired: "That connect attempt expired (they last 15 minutes). Try again.",
  bad_state: "Google sent back a connect attempt this server didn't start. Try again.",
  not_allowed: "The account that started this connect isn't allowed any more.",
  no_code: "Google didn't send a code back. Try again.",
  google_error: "Google returned an error instead of a code. Try again.",
  code_used: "That code was already used or has expired. Try again.",
  no_refresh_token:
    "Google didn't grant offline access. Remove Sports Tracker under myaccount.google.com, Security, Third-party access, then connect again.",
  not_configured: "Google connect isn't configured on this server.",
};

/**
 * What to tell the user on landing back from Google, or null when the URL is
 * not a connect return. The toast stays one short line; the error's reason is
 * `detail`, for the page to keep on screen.
 */
export function connectReturn(params: URLSearchParams): { toast: string; detail: string | null } | null {
  const outcome = params.get("google");
  if (outcome === "connected") return { toast: "Google Contacts connected", detail: null };
  if (outcome !== "error") return null;
  return { toast: "Google connect failed", detail: CONNECT_ERRORS[params.get("reason") ?? ""] ?? "Connecting Google failed. Try again." };
}
